import type { Ref } from 'vue';
import type { Recipe, WithContext } from 'schema-dts';
import { NUTRIENT_UNITS } from '@shared/app/utils/calcRecipeNutrients';
import { formatUtcToJstTimestamp } from '@shared/app/utils/dateFormatter';
import { useHead } from '#app/composables/head';
import type { RecipeDetailResponse } from '~~/server/api/kiwi_user/recipes/detail';
import { serializeJsonLd } from '~/utils/serializeJsonLd';

/**
 * レシピ詳細データからschema.orgのRecipe形式のJSON-LDを生成する
 * @see https://schema.org/Recipe
 * @see https://developers.google.com/search/docs/appearance/structured-data/recipe
 */
export function useRecipeJsonLd(
  recipe: Ref<RecipeDetailResponse | null | undefined>,
  mainImageUrls: Ref<string[]>,
) {
  useHead(() => {
    if (!recipe.value) {
      return {};
    }

    // 材料リストを生成（デフォルトグループ + その他グループ）
    const recipeIngredient = [
      ...(
        recipe.value.composition_group_default?.recipe_compositions ?? []
      ).map((c) => `${c.title} ${c.quantity}`),
      ...recipe.value.composition_group_others.flatMap((group) =>
        group.recipe_compositions.map((c) => `${c.title} ${c.quantity}`),
      ),
    ];

    // 作り方を生成
    const recipeInstructions = recipe.value.recipe_steps.map(
      (step, i) => `${i + 1}. ${step.title}`,
    );

    // schema.org Recipe構造化データ
    const jsonLd: WithContext<Recipe> = {
      '@context': 'https://schema.org',
      '@type': 'Recipe',
      name: recipe.value.title,
      image: mainImageUrls.value,
      description: recipe.value.lead ?? undefined,
      recipeYield: recipe.value.quantity.toString(),
      recipeIngredient,
      recipeInstructions,
      nutrition: {
        '@type': 'NutritionInformation',
        calories: `${recipe.value.nutrients.energy} ${NUTRIENT_UNITS.energy}`,
        fatContent: `${recipe.value.nutrients.lipid} ${NUTRIENT_UNITS.lipid}`,
        carbohydrateContent: `${recipe.value.nutrients.carbohydrate} ${NUTRIENT_UNITS.carbohydrate}`,
        cholesterolContent: `${recipe.value.nutrients.cholesterol} ${NUTRIENT_UNITS.cholesterol}`,
        fiberContent: `${recipe.value.nutrients.dietary_fibers_total} ${NUTRIENT_UNITS.dietary_fibers_total}`,
        proteinContent: `${recipe.value.nutrients.protein} ${NUTRIENT_UNITS.protein}`,
        saturatedFatContent: `${recipe.value.nutrients.fatty_acids_saturated} ${NUTRIENT_UNITS.fatty_acids_saturated}`,
        sodiumContent: `${recipe.value.nutrients.mineral_sodium} ${NUTRIENT_UNITS.mineral_sodium}`,
      },
    };

    // 調理時間がある場合はISO 8601形式で追加
    if (recipe.value.cooking_time) {
      jsonLd.totalTime = `PT${recipe.value.cooking_time}M`;
    }

    // 著者情報がある場合は追加
    if (recipe.value.author) {
      jsonLd.author = {
        '@type': 'Person',
        name: recipe.value.author.name,
      };
    }

    // 公開日がある場合はJST（+09:00）形式で追加
    if (recipe.value.published_at) {
      jsonLd.datePublished = formatUtcToJstTimestamp(recipe.value.published_at);
    }

    return {
      script: [
        {
          type: 'application/ld+json',
          innerHTML: serializeJsonLd(jsonLd),
        },
      ],
    };
  });
}
